/**
 * TypeScript interfaces for all API response shapes.
 * These mirror the server's OpenAPI schemas (server/src/openapi/schemas.ts).
 * All dates are ISO 8601 strings (serialized from Mongoose Dates).
 * All ObjectIds are serialized as strings.
 */

// ── Shared enums ─────────────────────────────────────────────────────────────

export type Severity = 'critical' | 'warning' | 'suggestion' | 'nitpick';
export type RiskLevel = 'low' | 'medium' | 'high';
export type ReviewStatus = 'queued' | 'processing' | 'completed' | 'failed';
export type ReviewTrigger = 'auto_open' | 'auto_push' | 'auto_reopen' | 'comment_command';
export type Plan = 'free' | 'pro' | 'enterprise' | 'custom';
export type AccountType = 'Organization' | 'User';

// ── User ─────────────────────────────────────────────────────────────────────

export interface User {
  id: string;
  githubId: number;
  login: string;
  name: string;
  email: string;
  avatarUrl: string;
  installationIds: string[];
  lastLoginAt: string;
  createdAt: string;
  updatedAt: string;
}

// ── Installation ──────────────────────────────────────────────────────────────

export interface InstallationSettings {
  defaultModel: string;
  autoReviewOnOpen: boolean;
  autoReviewOnPush: boolean;
  severityThreshold: Severity;
  maxCommentsPerReview: number;
}

export interface InstallationRepo {
  githubRepoId: number;
  fullName: string;
  isActive: boolean;
}

export interface Installation {
  id: string;
  githubInstallationId: number;
  accountLogin: string;
  accountType: AccountType;
  accountAvatarUrl: string;
  status: 'active' | 'suspended' | 'deleted';
  plan: Plan;
  settings: InstallationSettings;
  repositories: InstallationRepo[];
  createdAt: string;
  updatedAt: string;
}

// ── Review ────────────────────────────────────────────────────────────────────

export interface ReviewComment {
  path: string;
  line: number;
  side: 'LEFT' | 'RIGHT';
  severity: Severity;
  category: string;
  body: string;
  suggestedFix: string | null;
  startLine: number | null;
  githubCommentId: number | null;
}

export interface ReviewMetrics {
  filesTotal: number;
  filesReviewed: number;
  filesFiltered: number;
  tokensInput: number;
  tokensOutput: number;
  durationMs: number;
  costEstimateUsd: number;
}

export interface CommentCounts {
  critical: number;
  warning: number;
  suggestion: number;
  nitpick: number;
}

interface ReviewResultBase {
  summary: string;
  riskLevel: RiskLevel;
  estimatedImpact: string;
  commentCounts: CommentCounts;
}

export interface ReviewError {
  message: string;
  stage: string;
  attempts: number;
}

export type FixStatus = 'queued' | 'processing' | 'completed' | 'failed';
export type PrStatus = 'open' | 'merged' | 'closed';

export interface ReviewFix {
  status: FixStatus;
  fixBranch: string | null;
  fixPrNumber: number | null;
  fixPrUrl: string | null;
  fixPrStatus: PrStatus | null;
  filesFixed: string[];
  error: { message: string; stage: string } | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ReviewSummary {
  id: string;
  installationId: string;
  repositoryId: string;
  repoFullName: string;
  prNumber: number;
  prTitle: string;
  prAuthor: string;
  headSha: string;
  baseBranch: string;
  headBranch: string;
  status: ReviewStatus;
  trigger: ReviewTrigger;
  aiModel: string;
  prStatus: PrStatus;
  metrics: ReviewMetrics;
  result: ReviewResultBase | null;
  error: ReviewError | null;
  fix: ReviewFix | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ReviewDetail extends Omit<ReviewSummary, 'result'> {
  result: (ReviewResultBase & { comments: ReviewComment[] }) | null;
}

export interface PaginatedReviews {
  reviews: ReviewSummary[];
  total: number;
  page: number;
  limit: number;
}

// ── Repository settings ───────────────────────────────────────────────────────

export interface RepoSettings {
  model: string | null;
  ignorePatterns: string[];
  includeAlways: string[];
  customInstructions: string | null;
  maxFileSizeKb: number | null;
  reviewDrafts: boolean;
  severityThreshold: Severity | null;
  maxComments: number | null;
}

// ── Usage ─────────────────────────────────────────────────────────────────────

export interface UsageByModel {
  reviewCount: number;
  tokensInput: number;
  tokensOutput: number;
}

export interface Usage {
  installationId: string;
  period: string;
  reviewCount: number;
  tokensInput: number;
  tokensOutput: number;
  estimatedCostUsd: number;
  byModel: Record<string, UsageByModel>;
  fixCount: number;
  fixTokensInput: number;
  fixTokensOutput: number;
  estimatedFixCostUsd: number;
}

// ── API error ─────────────────────────────────────────────────────────────────

export interface ApiErrorBody {
  error: { message: string; code: string };
}

export class ApiError extends Error {
  constructor(
    public readonly message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Query params ──────────────────────────────────────────────────────────────

export interface ReviewListParams {
  installationId: string;
  repoId?: string;
  status?: ReviewStatus;
  riskLevel?: RiskLevel;
  search?: string;
  page?: number;
  limit?: number;
}
